import type { EditorPlugin } from '../interfaces/editor-plugin';
import type { FabricEditor } from '../core/fabric-editor';
import { FabricPluginName } from './plugin-name';

export interface DirtyFacade {
  isDirty(): boolean;
  markClean(): void;
  markDirty(): void;
}

declare module '../core/fabric-editor' {
  interface FabricEditor {
    dirty?: DirtyFacade;
  }
}

export function dirtyPlugin(): EditorPlugin {
  return {
    name: FabricPluginName.DIRTY,
    install(editor: FabricEditor) {
      let dirty = false;

      const set = (next: boolean) => {
        if (dirty === next) return;
        dirty = next;
        editor.emit('dirty:changed', { isDirty: next });
      };

      const onChange = () => set(true);
      const offAdded = editor.on('object:added', onChange);
      const offModified = editor.on('object:modified', onChange);
      const offRemoved = editor.on('object:removed', onChange);

      const facade: DirtyFacade = {
        isDirty: () => dirty,
        markClean: () => set(false),
        markDirty: () => set(true),
      };
      editor.dirty = facade;

      (facade as DirtyFacade & { __off?: () => void }).__off = () => {
        offAdded();
        offModified();
        offRemoved();
      };
    },
    dispose(editor: FabricEditor) {
      const facade = editor.dirty as (DirtyFacade & { __off?: () => void }) | undefined;
      facade?.__off?.();
      editor.dirty = undefined;
    },
  };
}
